if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[data-gallery-dots]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.sortMediaByAltMarker();
        window.setTimeout(() => {
          this.elements.viewer?.resetPages?.();
          this.elements.thumbnails?.resetPages?.();
          this.setInitialMediaFromMarkerOrder();
          this.dataset.initialMediaResolved = 'true';
        }, 0);
        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailEvents();
        this.initGalleryDots();
        this.initImageZoom();
        this.initThumbnailOverflowIndicator();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      sortMediaByAltMarker() {
        const markerPriority = {
          '#bg-featured': 0,
          '#studio': 1,
          '#original': 2,
          '#bg': 3,
          '#size': 4,
        };

        const getPriority = (item) => {
          const marker = (item.dataset.mediaAlt || '').split(/\s+/).find((token) => Object.prototype.hasOwnProperty.call(markerPriority, token));
          return marker ? markerPriority[marker] : 99;
        };

        const sortList = (list, positionAttribute) => {
          if (!list) return;
          const items = Array.from(list.children);
          items.sort((a, b) => getPriority(a) - getPriority(b));
          items.forEach((item, index) => {
            list.appendChild(item);
            if (positionAttribute) item.dataset.mediaPosition = index + 1;
          });
        };

        sortList(this.elements.thumbnails?.querySelector('ul'), true);
        sortList(this.elements.viewer?.querySelector('ul'), false);
      }

      setInitialMediaFromMarkerOrder() {
        const firstMedia = this.elements.viewer?.querySelector('.product__media-item[data-media-id]');
        if (!firstMedia) return;

        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((item) => {
          item.classList.toggle('is-active', item === firstMedia);
        });

        const firstThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${firstMedia.dataset.mediaId}"]`);
        this.elements.thumbnails?.querySelectorAll('button[aria-current]').forEach((button) => button.removeAttribute('aria-current'));
        firstThumbnail?.querySelector('button')?.setAttribute('aria-current', 'true');
        this.setActiveDot(firstMedia.dataset.mediaId);
      }

      bindThumbnailEvents() {
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.mediaGalleryBound === 'true') return;

          button.dataset.mediaGalleryBound = 'true';
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.setActiveDot(mediaId);
      }

      setActiveMedia(mediaId, prepend) {
        const mediaSelector = '.product__media-item[data-media-id]';
        const activeMedia =
          this.elements.viewer.querySelector(`${mediaSelector}[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector(mediaSelector);
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll(mediaSelector).forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.setActiveDot(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.mql.matches) {
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const scrollTop = this.elements.thumbnails.scrollTop;
          const visibleBottom = scrollTop + this.elements.thumbnails.clientHeight;

          if (thumbnailTop >= scrollTop + 10 && thumbnailBottom <= visibleBottom - 10) return;

          this.elements.thumbnails.scrollTo({
            top: thumbnailTop - this.elements.thumbnails.clientHeight / 2 + thumbnail.offsetHeight / 2,
            behavior: 'smooth',
          });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initGalleryDots() {
        if (!this.elements.dots || !this.elements.viewer) return;
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')).filter((item) => {
          if (item.hasAttribute('hidden')) return false;
          return item.clientWidth > 0 && item.clientHeight > 0;
        });
        if (mediaItems.length <= 1) {
          this.elements.dots.parentElement?.setAttribute('hidden', '');
          return;
        }

        this.elements.dots.innerHTML = '';
        mediaItems.forEach((item, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'naturlich-gallery__dot';
          dot.setAttribute('aria-label', `Go to media ${index + 1}`);
          dot.dataset.target = item.dataset.mediaId;
          if (item.classList.contains('is-active')) dot.classList.add('is-active');
          dot.addEventListener('click', () => this.setActiveMedia(item.dataset.mediaId, false));
          const li = document.createElement('li');
          li.appendChild(dot);
          this.elements.dots.appendChild(li);
        });
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots) return;
        this.elements.dots.querySelectorAll('.naturlich-gallery__dot').forEach((dot) => {
          dot.classList.toggle('is-active', dot.dataset.target === mediaId);
        });
      }

      initThumbnailOverflowIndicator() {
        if (!this.elements.thumbnails) return;

        const updateIndicatorState = () => {
          if (!this.mql.matches) {
            this.elements.thumbnails.classList.remove('has-overflow', 'is-at-end');
            return;
          }

          const hasOverflow =
            this.elements.thumbnails.scrollHeight - this.elements.thumbnails.clientHeight > 4;
          const isAtEnd =
            this.elements.thumbnails.scrollTop + this.elements.thumbnails.clientHeight >=
            this.elements.thumbnails.scrollHeight - 4;

          this.elements.thumbnails.classList.toggle('has-overflow', hasOverflow);
          this.elements.thumbnails.classList.toggle('is-at-end', hasOverflow ? isAtEnd : false);
        };

        this.elements.thumbnails.addEventListener('scroll', updateIndicatorState, { passive: true });
        window.addEventListener('resize', updateIndicatorState);
        this.mql.addEventListener('change', updateIndicatorState);
        window.setTimeout(updateIndicatorState);
      }

      initImageZoom() {
        this.elements.viewer?.querySelectorAll('.product__modal-opener--image').forEach((opener) => {
          const media = opener.querySelector('.product__media');
          const image = media?.querySelector('img.image-magnify-lightbox');
          const toggle = opener.querySelector('.product__media-toggle.product__media-zoom-lightbox');
          if (!media || !image || media.dataset.naturlichZoomBound === 'true') return;

          media.dataset.naturlichZoomBound = 'true';
          toggle?.remove();

          opener.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
            this.toggleImageZoom(opener, media, image, event);
          }, { capture: true });

          media.addEventListener('click', (event) => {
            if (event.target.closest('button, a')) return;
            this.toggleImageZoom(opener, media, image, event);
          });
        });
      }

      toggleImageZoom(opener, media, image, event) {
        const existingOverlay = media.querySelector('.image-magnify-full-size');
        const clearTracking = () => {
          media.onpointermove = null;
          media.onmousemove = null;
          media.onmouseleave = null;
          opener.onpointermove = null;
          opener.onmousemove = null;
          opener.onmouseleave = null;
          document.onpointermove = null;
          document.onmousemove = null;
        };

        if (existingOverlay) {
          existingOverlay.remove();
          image.style.opacity = '100%';
          clearTracking();
          return;
        }

        const overlay = document.createElement('div');
        overlay.className = 'image-magnify-full-size';
        overlay.setAttribute('aria-hidden', 'true');
        overlay.style.backgroundImage = `url('${image.currentSrc || image.src}')`;
        overlay.style.backgroundColor = 'var(--gradient-background)';
        image.style.opacity = '50%';
        media.prepend(overlay);

        const updateZoomPosition = (pointerEvent) => {
          const rect = media.getBoundingClientRect();
          const xPercent = ((pointerEvent.clientX - rect.left) / rect.width) * 100;
          const yPercent = ((pointerEvent.clientY - rect.top) / rect.height) * 100;
          overlay.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
          overlay.style.backgroundSize = `${image.naturalWidth * 2}px`;
        };

        const removeZoom = () => {
          overlay.remove();
          image.style.opacity = '100%';
          clearTracking();
        };

        updateZoomPosition(event);
        opener.onpointermove = updateZoomPosition;
        opener.onmousemove = updateZoomPosition;
        opener.onmouseleave = removeZoom;
        media.onpointermove = updateZoomPosition;
        media.onmousemove = updateZoomPosition;
        document.onpointermove = updateZoomPosition;
        document.onmousemove = updateZoomPosition;
        overlay.addEventListener('mousemove', updateZoomPosition);
      }

    }
  );
}
